import { Button } from "@/dentlab/src/components/ui/button";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import { Loader2 } from "lucide-react";
import { ConnectBeforeShareDialog } from "./connect-before-share-dialog.component";
import { useEmailCheckAction } from "./useEmailCheckAction";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { useJobDocumentActions } from "@/dentlab/src/hooks/actions/useJobDocumentActions";
import { JobStatusEnum } from "@/lib/types/job";
import {
	PdfTemplateType,
	XmlTemplateType,
} from "@/dentlab/src/pdf-templates/document-template.types";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import { HandleExportDocumentResult } from "@/dentlab/src/hooks/useHandleExportDocument/useHandleExportDocument";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePostHog } from "posthog-js/react";

interface EmailCheckButtonProps {
	jobDocument: JobDocumentWithFiles;
	handleExportDocument: (
		templateType: PdfTemplateType | XmlTemplateType,
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentWithFiles
	) => Promise<HandleExportDocumentResult>;
	jobItems: JobItemEntityType[];
}

export const EmailCheckButton: React.FC<EmailCheckButtonProps> = ({
	jobDocument,
	handleExportDocument,
	jobItems,
}) => {
	const postHog = usePostHog();
	const { isExportJobDocumentAllowed } = useJobDocumentActions();
	const { pendingChanges } = useJobStore((state) => ({
		pendingChanges: state.pendingChanges,
	}));
	const { isAllowed, explanation } = isExportJobDocumentAllowed(
		jobDocument,
		jobItems
	);

	const { client } = useCentralStore((state) => ({
		client: state.client,
	}));

	const {
		emailActionLoading,
		showConnectDialog,
		setShowConnectDialog,
		handleEmailCheckClick,
		handleConnectionSuccess,
		connectedOrganizationEmail,
	} = useEmailCheckAction({
		jobDocument,
		jobItems,
		handleExportDocument,
	});

	const sendEmailOnShare = client?.is_send_email_on_share !== false;

	let tooltipText = sendEmailOnShare
		? connectedOrganizationEmail
			? `Dokument exportieren, teilen und per E-Mail an ${connectedOrganizationEmail} senden`
			: "Dokument exportieren, teilen und per E-Mail senden"
		: "Dokument exportieren und teilen (ohne E-Mail-Benachrichtigung)";

	// To make sure user can't click while exporting, also disable when completed
	let isDisabled =
		!isAllowed ||
		emailActionLoading ||
		jobDocument.status === JobStatusEnum.COMPLETED;

	if (emailActionLoading) {
		tooltipText = "Dokument wird exportiert und geteilt...";
	} else if (pendingChanges) {
		tooltipText = "Änderungen werden gespeichert...";
		isDisabled = true;
	} else if (!isAllowed) {
		tooltipText = explanation;
	}

	return (
		<>
			<TooltipWrapper title={tooltipText} position="bottom">
				<span>
					<Button
						variant="default"
						size="sm"
						className="h-7 px-2 text-xs"
						onClick={() => {
							postHog.capture("email_check_export_share", {
								document_type: jobDocument.type,
							});
							handleEmailCheckClick();
						}}
						disabled={isDisabled}
						data-testid="email-check-button"
						aria-label="Button to export, share and email job document"
					>
						{emailActionLoading && (
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
						)}
						Teilen
					</Button>
				</span>
			</TooltipWrapper>

			<ConnectBeforeShareDialog
				open={showConnectDialog}
				onClose={() => setShowConnectDialog(false)}
				onConnectionSuccess={handleConnectionSuccess}
			/>
		</>
	);
};
